// =============================================================================
// BP-07-03: ResourceBreadcrumb Component
// Navigation breadcrumbs for resources and diagnoses
// =============================================================================

import { Link } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import type { Resource, ResourceCategory, Diagnosis } from '@/features/resources/types/resourcesTypes';
import { cn } from '@/core/lib/utils';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface ResourceBreadcrumbProps {
  items?: BreadcrumbItem[];
  resource?: Resource;
  category?: ResourceCategory;
  diagnosis?: Diagnosis;
  showHome?: boolean;
  className?: string;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function ResourceBreadcrumb({
  items,
  resource,
  category,
  diagnosis,
  showHome = true,
  className,
}: ResourceBreadcrumbProps) {
  // Build breadcrumb items from props
  const breadcrumbs: BreadcrumbItem[] = [];

  // Always start with home
  if (showHome) {
    breadcrumbs.push({ label: 'Home', href: '/' });
  }

  // Custom items take precedence
  if (items && items.length > 0) {
    breadcrumbs.push(...items);
  } else {
    // Build from resource/category/diagnosis
    if (diagnosis) {
      breadcrumbs.push({ label: 'Diagnoses', href: '/diagnoses' });
      breadcrumbs.push({ label: diagnosis.name, href: `/diagnoses/${diagnosis.slug}` });
    } else if (resource) {
      breadcrumbs.push({ label: 'Resources', href: '/resources' });
      
      if (resource.category) {
        breadcrumbs.push({
          label: resource.category.name,
          href: `/resources/category/${resource.category.slug}`,
        });
      }
      
      breadcrumbs.push({ label: resource.title });
    } else if (category) {
      breadcrumbs.push({ label: 'Resources', href: '/resources' });
      
      // Add parent categories if available
      if (category.parentId) {
        // Note: Would need parent category data to show full path
        // For now, just show current category
      }
      
      breadcrumbs.push({ label: category.name });
    }
  }

  if (breadcrumbs.length === 0) {
    return null;
  }

  return (
    <nav
      className={cn('flex items-center gap-1 text-sm', className)}
      aria-label="Breadcrumb"
    >
      <ol className="flex items-center gap-1 flex-wrap">
        {breadcrumbs.map((item, index) => {
          const isLast = index === breadcrumbs.length - 1;
          const isFirst = index === 0;

          return (
            <li key={index} className="flex items-center gap-1">
              {/* Separator */}
              {!isFirst && (
                <ChevronRight
                  className="w-4 h-4 text-warm-gray/50 flex-shrink-0"
                  aria-hidden="true"
                />
              )}

              {/* Item */}
              {item.href && !isLast ? (
                <Link
                  to={item.href}
                  className={cn(
                    'flex items-center gap-1 text-warm-gray hover:text-sage transition-colors',
                    isFirst && 'text-navy'
                  )}
                >
                  {isFirst && showHome && (
                    <Home className="w-4 h-4" aria-hidden="true" />
                  )}
                  <span className={isFirst && showHome ? 'sr-only sm:not-sr-only' : ''}>
                    {item.label}
                  </span>
                </Link>
              ) : (
                <span
                  className={cn(
                    'text-navy font-medium',
                    isLast && 'line-clamp-1'
                  )}
                  aria-current={isLast ? 'page' : undefined}
                >
                  {item.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

// -----------------------------------------------------------------------------
// Compact Breadcrumb (mobile-friendly)
// -----------------------------------------------------------------------------

interface CompactBreadcrumbProps {
  backLabel?: string;
  backHref: string;
  currentLabel: string;
  className?: string;
}

export function CompactBreadcrumb({
  backLabel = 'Back',
  backHref,
  currentLabel,
  className,
}: CompactBreadcrumbProps) {
  return (
    <nav className={cn('flex items-center gap-2 text-sm', className)} aria-label="Breadcrumb">
      <Link
        to={backHref}
        className="flex items-center gap-1 text-sage hover:text-sage-dark transition-colors"
      >
        <ChevronRight className="w-4 h-4 rotate-180" />
        <span>{backLabel}</span>
      </Link>
      <span className="text-warm-gray">/</span>
      <span className="text-navy font-medium line-clamp-1" aria-current="page">
        {currentLabel}
      </span>
    </nav>
  );
}

// -----------------------------------------------------------------------------
// Structured Data (JSON-LD for SEO)
// -----------------------------------------------------------------------------

interface BreadcrumbStructuredDataProps {
  items: BreadcrumbItem[];
  baseUrl?: string;
}

export function BreadcrumbStructuredData({
  items,
  baseUrl = 'https://brightpath.com',
}: BreadcrumbStructuredDataProps) {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      item: item.href ? `${baseUrl}${item.href}` : undefined,
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
    />
  );
}

export default ResourceBreadcrumb;

